import type { ComponentChildren, Ref } from "preact";
import { useEffect, useId, useRef } from "preact/hooks";
import { cx } from "../cx";

export interface TextInputProps {
  value: string;
  onValue: (value: string) => void;
  type?: "text" | "password";
  id?: string;
  name?: string;
  placeholder?: string;
  maxLength?: number;
  autoComplete?: string;
  ariaLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Focus on mount. Menus that open a dialog use `data-autofocus` instead. */
  autoFocus?: boolean;
  /** Called when Enter is pressed. */
  onEnter?: () => void;
  inputRef?: Ref<HTMLInputElement>;
  class?: string;
}

export function TextInput({
  value,
  onValue,
  type = "text",
  id,
  name,
  placeholder,
  maxLength,
  autoComplete,
  ariaLabel,
  disabled = false,
  invalid = false,
  autoFocus = false,
  onEnter,
  inputRef,
  class: cls,
}: TextInputProps) {
  const local = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocus) local.current?.focus();
  }, []);
  const setRef = (el: HTMLInputElement | null) => {
    local.current = el;
    if (typeof inputRef === "function") inputRef(el);
    else if (inputRef) inputRef.current = el;
  };
  // Preact types <input> as a union keyed on the literal `type`, so password and text are
  // rendered from the same attributes in two branches instead of passing a union-typed `type`.
  const shared = {
    ref: setRef,
    id,
    name,
    class: cx("input", invalid && "is-invalid", cls),
    value,
    placeholder,
    maxLength,
    autoComplete,
    "aria-label": ariaLabel,
    "aria-invalid": invalid || undefined,
    disabled,
    spellcheck: false,
    onInput: (e: Event & { currentTarget: HTMLInputElement }) => onValue(e.currentTarget.value),
    onKeyDown: (e: KeyboardEvent) => {
      if (e.isComposing || e.keyCode === 229) return; // Enter that confirms an IME composition is not a submit
      if (e.key === "Enter" && onEnter) {
        e.preventDefault();
        onEnter();
      }
    },
    "data-nav": "",
  };
  return type === "password" ? <input {...shared} type="password" /> : <input {...shared} type="text" />;
}

export interface FieldProps {
  label: ComponentChildren;
  /** Static helper text. Replaced by `error` when present. */
  hint?: ComponentChildren;
  error?: ComponentChildren;
  children: (id: string) => ComponentChildren;
  class?: string;
}

/**
 * Label + control + hint/error. The hint line is always rendered (reserving its height) so that
 * showing an error never shifts the layout. `children` receives the id to put on the control.
 */
export function Field({ label, hint, error, children, class: cls }: FieldProps) {
  const id = useId();
  return (
    <div class={cx("field", error ? "has-error" : null, cls)}>
      <label class="field__label" for={id}>
        {label}
      </label>
      {children(id)}
      <span class="field__hint" role={error ? "alert" : undefined}>
        {error ?? hint ?? " "}
      </span>
    </div>
  );
}
